import { useTranslation as useI18nTranslation } from 'react-i18next';
import { translationKeyExists } from '../utils/i18nUtils';

// Define a type for our custom translation function
type CustomTFunction = (
  key: string,
  defaultValueOrOptions?: string | object,
  options?: object
) => string;

/**
 * Custom hook that wraps the useTranslation hook to make it easier to use specific namespaces
 *
 * @param namespace - The namespace to use for translations
 * @returns The translation function and i18n instance
 */
export const useTranslationWithNamespace = (namespace?: string): { t: CustomTFunction; i18n: any } => {
  // Use the default useTranslation hook with the namespace if provided
  const { t: originalT, i18n } = useI18nTranslation(namespace);

  // Create a wrapper function that adds the namespace to the key if provided
  // This function accepts the same parameters as the original t function:
  // - key: string - The translation key
  // - defaultValue?: string - The default value if the key is not found
  // - options?: object - Additional options like count, context, etc.
  const t = (key: string, defaultValueOrOptions?: string | object, options?: object) => {
    // If namespace is provided and key doesn't already include a namespace
    if (namespace && !key.includes(':')) {
      // Check if the key exists in development mode
      if (process.env.NODE_ENV === 'development') {
        translationKeyExists(key, namespace);
      }

      const namespaceKey = `${namespace}:${key}`;

      // Handle different parameter combinations
      if (typeof defaultValueOrOptions === 'string') {
        // Case: t(key, defaultValue, options)
        // We need to merge the defaultValue and options for react-i18next
        const mergedOptions = options ? { defaultValue: defaultValueOrOptions, ...options } : { defaultValue: defaultValueOrOptions };
        return originalT(namespaceKey, mergedOptions);
      } else if (defaultValueOrOptions) {
        // Case: t(key, options)
        return originalT(namespaceKey, defaultValueOrOptions);
      } else {
        // Case: t(key)
        return originalT(namespaceKey);
      }
    }

    // If no namespace or key already has namespace
    if (typeof defaultValueOrOptions === 'string') {
      // Case: t(key, defaultValue, options)
      const mergedOptions = options ? { defaultValue: defaultValueOrOptions, ...options } : { defaultValue: defaultValueOrOptions };
      return originalT(key, mergedOptions);
    } else if (defaultValueOrOptions) {
      // Case: t(key, options)
      return originalT(key, defaultValueOrOptions);
    } else {
      // Case: t(key)
      return originalT(key);
    }
  };

  return { t, i18n };
};
